// @flow
import { createMemoryHistory } from "history";
import { has } from "../utils/helpers/objects";
import deepmerge from "deepmerge";

import configureStore from "../redux/store/configureStore";

import Locales, { availableLocales } from "../i18n/Locales";

import { getPreferredLocale } from "./requestInformation";
import { setLocales } from "../redux/_i18n/I18nActions";

import {
  setServerPreference,
  setThemePreference,
  setPreference,
  setAllContentInDataSetting,
  setLoginPreferences,
  setDateTimeSettings,
} from "../redux/actions/Preferences";

import { getEnabledLocales } from "../constants/Settings";

import IllegalArgumentException from "../exceptions/IllegalArgumentException";

import { initModels } from "../redux/_modularui/ModularUIActions";
import { getLocale } from "../redux/selectors/i18n";

import ModularUIRequest from "../modularui/ModularUIRequest";

import type { LocaleConfiguration } from "../i18n/types";
import type { ReduxState, ReduxStore } from "../redux/types";
import type Href from "../models/href/Href";
import type { RouterHistory } from "react-router";

export type PreferenceValue = {
  name: string,
  defaultValue?: string | null,
  type?: string,
};

/**
 */
const createReduxStore = (
  requestHref: Href,
  customReducers: Object,
  initialState?: $Shape<ReduxState>,
): ReduxStore => {
  // $FlowExpectedError
  const history: RouterHistory = createMemoryHistory({
    initialEntries: [requestHref.toString()],
  });

  const { store } = configureStore(history, customReducers, initialState);

  return store;
};

/**
 */
const setI18n = (
  store: ReduxStore,
  locales: Array<LocaleConfiguration> = [],
  request: HttpServletRequestJava,
) => {
  if (!Array.isArray(locales)) {
    throw new IllegalArgumentException("locales must be an array of objects");
  }
  if (!request) {
    throw new IllegalArgumentException("missing request argument");
  }

  const mergedLocales = availableLocales.map((locale) => {
    const code = locale.code;
    const customLocale = locales.find((custLocale) => custLocale.code === code);

    if (customLocale) {
      return deepmerge(locale, customLocale);
    }

    return locale;
  });

  locales.forEach((customLocale) => {
    if (!mergedLocales.some((locale) => locale.code === customLocale.code)) {
      mergedLocales.push(customLocale);
    }
  });

  const enabledLocales = mergedLocales.filter((locale: LocaleConfiguration) =>
    getEnabledLocales().includes(locale.code),
  );

  const localesInstance = new Locales(enabledLocales);
  const preferredLocale = getPreferredLocale(request, localesInstance);
  store.dispatch(setLocales(localesInstance, preferredLocale ?? "en"));
};

const setServerPreferencesFromArray = (
  store: ReduxStore,
  serverPreferences: Array<string | PreferenceValue>,
) => {
  serverPreferences.forEach((serverPreference) => {
    if (typeof serverPreference === "object") {
      const { name, defaultValue, type } = serverPreference;
      store.dispatch(setServerPreference(name, defaultValue, type));
    } else {
      store.dispatch(setServerPreference(serverPreference, null));
    }
  });
};

/**
 * Server preferences can be suplied by an array of preference names,
 * or an array of objects which has the name, an optional default value and an optional type.<br>
 * When the type is set to string, a comma separated value is not converted to an array:
 *
 * @example
 * // value becomes an array [default1, default2]
 * { name: "prefName", defaultValue: "default1, default2" }
 * // value remains a string "default1, default2"
 * { name: "prefName", defaultValue: "default1, default2", type: "string" }
 *
 * @param store
 * @param serverPreferences
 */
const setServerPreferences = (
  store: ReduxStore,
  serverPreferences: Array<string | PreferenceValue>,
) => {
  if (Array.isArray(serverPreferences)) {
    setServerPreferencesFromArray(store, serverPreferences);
  }

  store.dispatch(setAllContentInDataSetting());
  store.dispatch(setLoginPreferences());
  store.dispatch(setDateTimeSettings());

  // $FlowIssue
  if (typeof globalThis.preferencesProvider?.isStudioContext === "function") {
    store.dispatch(
      setPreference(
        "isStudioContext",
        // $FlowIssue
        globalThis.preferencesProvider?.isStudioContext(),
      ),
    );
  }
};

/**
 */
const setConfigurationTheme = (store: ReduxStore) => {
  let configTheme = null;

  const configFileLocation =
    // $FlowIssue
    globalThis.preferencesProvider?.getLayoutConfigFileLocation();
  if (typeof configFileLocation === "string") {
    const configFilePath = configFileLocation.startsWith("/")
      ? `/resource${configFileLocation}`
      : `/resource/${configFileLocation}`;

    configTheme = dataFetcher.fetch(configFilePath);

    if (configTheme) {
      store.dispatch(setThemePreference(configTheme));
    } else {
      throw new Error(
        `Could not read theme configuration file from: ${configFilePath}`,
      );
    }
  }
};

/**
 */
const setApplication = (store: ReduxStore) => {
  try {
    const locale = getLocale(store.getState());
    const application = new ModularUIRequest("/", {
      locale: locale,
    }).fetchSync();

    application.connectKey = `application(/)(${locale})`;

    store.dispatch(
      initModels([
        {
          key: `application(/)(${locale})`,
          model: application,
        },
      ]),
    );
  } catch (e) {
    // swallow error, in this case we just don't pre-cache the webapplication,
    // which has not much difference for the end user
  }
};

/**
 */
const handleErrors = (store: ReduxStore) => {
  const state = store.getState();

  if (
    state.error &&
    (state.error.shouldThrowOnServer ||
      !has(state.error, "shouldThrowOnServer"))
  ) {
    throw state.error;
  }
};

/**
 */
const dehydrate = (store: ReduxStore): string => {
  const state = store.getState();

  const modularui: {
    [key: string]: { status: string, lastModification: number, model: ?Object },
  } = {};

  for (const key in state.modularui) {
    const { status, model, lastModification } = state.modularui[key];

    if (model) {
      modularui[key] = {
        status,
        lastModification,
        model: model.dehydrate(),
      };
    } else {
      modularui[key] = {
        status,
        lastModification,
        model: undefined,
      };
    }
  }

  const filteredState = {
    ...state,
    modularui,
  };

  return JSON.stringify(filteredState).replace(/</g, "\\u003c");
};

/**
 */
const createHead = (
  sheet: any,
  UUID: string,
  helmetContext?: Object,
): Object => {
  const style = sheet.getStyleTags();
  const meta = helmetContext ? [...helmetContext.helmet.meta] : [];

  if (helmetContext) {
    return {
      ...helmetContext.helmet,
      style,
      meta,
    };
  }

  return {
    style,
    meta,
  };
};

export {
  createReduxStore,
  setI18n,
  setApplication,
  setServerPreferences,
  setConfigurationTheme,
  createHead,
  handleErrors,
  dehydrate,
};
